import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { authFetch } from '../utils/api';
import { buildApiUrl } from '../config/api';

interface UserFormProps {
  user?: User | null;
  onSave: () => void;
  onCancel: () => void;
  onNarrative: (message: string, type: 'info' | 'success' | 'error') => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onSave, onCancel, onNarrative }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const API_URL = buildApiUrl('/users');

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email || '');
    } else {
      setName('');
      setEmail('');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      onNarrative('⚠️ ERROR: Name field cannot be empty!', 'error');
      return;
    }

    setSaving(true);
    const isUpdate = !!user;
    
    onNarrative(
      isUpdate 
        ? `🔄 UPDATING USER #${user.id}... Rewriting their digital DNA...`
        : '🚀 LAUNCHING NEW USER INTO THE DATABASE STRATOSPHERE...',
      'info'
    );

    try {
      const url = isUpdate ? `${API_URL}/${user.id}` : API_URL;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        body: JSON.stringify({ 
          name: name.trim(), 
          email: email.trim() || null 
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Operation failed');
      }

      const result = await response.json();
      
      onNarrative(
        isUpdate 
          ? `✅ USER #${user.id} SUCCESSFULLY MUTATED!`
          : `🎉 USER #${result.id} BORN INTO EXISTENCE!`,
        'success'
      );
      
      onSave();
    } catch (error) {
      onNarrative(
        `💥 OPERATION FAILED: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border-2 border-blue-500">
      <h2 className="text-2xl font-bold mb-4">
        {user ? `🔧 MODIFY USER #${user.id}` : '➕ CREATE NEW USER'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter user name..."
            disabled={saving}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter email (optional)..."
            disabled={saving}
          />
        </div>
        
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className={`flex-1 py-2 px-4 rounded font-bold transition-colors ${
              saving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {saving ? '⚡ PROCESSING...' : user ? '💾 UPDATE USER' : '🚀 CREATE USER'}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors font-bold"
          >
            ❌ CANCEL
          </button>
        </div>
      </form>
    </div>
  );
};